import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useContaCilEmpresaContexto } from '../../../../hooks/useContaCilEmpresas';
import { IEmpresaOption } from '../../../../hooks/useContaCilImportar';
import { IExtratoStatus, useContaCilExtratoDetalhe } from '../../../../hooks/useContaCilExtratos';

/**
 * Status do extrato -> valor do KPI que o contém na tela de Extratos.
 * Dois cards agregam mais de um status, e o filtro espera a lista separada por
 * vírgula exatamente como o KPI monta — daí o mapa explícito em vez de usar o
 * status cru: mandar `aguardando_titular` sozinho filtraria certo, mas deixaria
 * o KPI "A identificar" sem destaque e o operador sem saber onde está.
 */
const STATUS_PARA_KPI: Record<string, string> = {
  concluido: 'concluido',
  processando: 'processando',
  a_identificar: 'a_identificar,aguardando_titular',
  aguardando_titular: 'a_identificar,aguardando_titular',
  aguardando_validacao: 'aguardando_validacao',
  erro: 'erro,quarentena',
  quarentena: 'erro,quarentena',
};

interface Posicionamento {
  /** Empresa do extrato, quando ele já está vinculado. */
  empresa: IEmpresaOption | null;
  /** KPI que contém o status do extrato. */
  kpi: string;
  status: IExtratoStatus;
}

/**
 * Resolve `?extrato=<id>` POSICIONANDO a lista no card, em vez de abrir o
 * detalhe por cima dela.
 *
 * Abrir o modal direto (primeira versão) mostrava o extrato mas escondia o
 * contexto: ao fechar, o operador caía numa lista sem filtro nenhum e sem
 * ideia de onde aquele documento estava. O que ele precisa ver é o card no
 * seu lugar — com os filtros que o alcançam já aplicados.
 *
 * Aplica, uma vez, o que o próprio extrato determina:
 *  - **empresa**, quando vinculado (recorta a lista ao contexto dele);
 *  - **KPI do status** — é o que resolve os casos "a identificar" e
 *    "aguardando", em que não há empresa para filtrar e o card só aparece pelo
 *    status;
 *  - **ordem recente**, senão um import de hoje nasce na última página do
 *    padrão (que ordena do mais antigo para o mais novo).
 *
 * A tela decide os filtros a partir do DETALHE do extrato, não da URL: quem
 * navega precisa saber só o id, e a regra de posicionamento mora num lugar só.
 */
export function usePosicionarNoExtratoParam(aplicar: (p: Posicionamento) => void): number | null {
  const [params] = useSearchParams();
  const raw = params.get('extrato');
  const id = raw ? Number(raw) : NaN;
  const extratoId = Number.isFinite(id) && id > 0 ? id : undefined;

  const { data: extrato } = useContaCilExtratoDetalhe(extratoId, extratoId != null);
  // Só busca contexto quando há empresa: extrato 'a identificar' não tem.
  const { data: ctx } = useContaCilEmpresaContexto(extrato?.empresa_id ?? null);

  const aplicado = useRef(false);

  useEffect(() => {
    if (aplicado.current || !extrato) return;
    // Com empresa vinculada, espera o contexto pra montar a opção completa do
    // picker (razão social + external_id). Sem ele o campo apareceria vazio.
    if (extrato.empresa_id && !ctx) return;
    aplicado.current = true;
    aplicar({
      empresa: ctx
        ? {
            value: ctx.empresa_id,
            label: ctx.razao_social || ctx.nome || `Empresa ${ctx.empresa_id}`,
            external_id: ctx.external_id || '',
          }
        : null,
      kpi: STATUS_PARA_KPI[extrato.status] ?? '',
      status: extrato.status,
    });
  }, [extrato, ctx, aplicar]);

  return extratoId ?? null;
}
